import { SocialAccountStatus, UserId, UserSocialAccountResponse, UserSocialId } from '@business/entities/account';
import { CommonService, UserService, HTMLService } from '@business/services';
import { Config } from '@config';
import { Button } from '@core/components/Button';
import { TopLoadingBar } from '@core/components/LoadingBar';
import { useBloc } from '@core/utils/bloc';
import { AppConfig } from '@core/utils/config';
import { useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { openSelectRTEModal } from '@modules/common/SelectRTEModal';
import { TableActions } from '@modules/common/TableActions';
import { MDBDataTable } from 'mdbreact';
import React, { memo, useCallback, useMemo } from 'react';
import { Col, Row, UncontrolledTooltip } from 'reactstrap';
import { NylasAccountsBloc } from './NylasAccountsBloc';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { openGenericMessageModal } from '@modules/common/GenericMessageModal';

export const NylasConnectUrl = (userId: UserId) => {
  const config = AppConfig.get();
  return window.location.replace(`${Config.GC_URL}/${config.appId}/user-social/connect/${userId}`);
};

export interface NylasAccountsProps {
  userId: UserId;
}

export const NylasAccounts = memo((props: NylasAccountsProps) => {
  const { userId } = props;
  const bloc = useBloc(NylasAccountsBloc, userId);
  const user = useObservable(bloc.user$);
  const loading = useLoadingState(bloc.loading);
  const onDelete = useCallback((id: UserSocialId) => bloc.deleteSocialAccount(id).subscribe(), [bloc]);
  const onUpdateSignature = useCallback((id: UserSocialId, signature: string) => bloc.updateSocialSignature(id, signature), [bloc]);
  const settings = useSettings();
  const emailSyncNumber = useMemo(() => (settings ? R.toNumber(settings.accountSettings.emailSyncNumber) : 0), [settings]);
  const totalSocialAccounts = useMemo(() => (user ? user.socialAccounts.length : 0), [user]);
  const isLimitedSync = useMemo(() => !emailSyncNumber || emailSyncNumber == 0, [emailSyncNumber]);
  const allowConnect = useMemo(
    () => isLimitedSync || (emailSyncNumber > 0 && totalSocialAccounts < emailSyncNumber),
    [emailSyncNumber, isLimitedSync, totalSocialAccounts],
  );

  const data = useMemo(
    () => ({
      columns: [
        {
          label: 'Email',
          field: 'email',
          sort: 'asc',
        },
        {
          label: 'Provider',
          field: 'provider',
          sort: 'asc',
        },
        {
          label: 'Status',
          field: 'status',
          sort: 'asc',
        },
        {
          label: 'Signature',
          field: 'signature',
          sort: 'asc',
        },
        {
          label: 'Action',
          field: 'action',
          sort: 'asc',
        },
      ],

      rows: R.map(user?.socialAccounts, (socialAccount: UserSocialAccountResponse) => ({
        email: socialAccount.email,
        // provider: `<span className="badge badge-info" style={{ backgroundColor: ${UserService.getSocialProviderColor(
        //   socialAccount.provider,
        // )} }>${R.upperFirst(socialAccount.provider)}</span>`,
        provider: R.upperFirst(socialAccount.provider),
        status: (
          <div className="d-flex" style={{ justifyContent: 'space-between' }}>
            <div>
              <span className={`badge badge-${UserService.getSocialStatusBadge(socialAccount.status)}`}>
                {UserService.getSocialStatusTitle(socialAccount.status)}
              </span>
            </div>
            {(socialAccount.status === SocialAccountStatus.invalid || socialAccount.status === SocialAccountStatus.stopped) && (
              <div>
                <Button id={`social-${socialAccount.id}`} extraClassNames={'btn-sm'} onClick={() => NylasConnectUrl(userId)}>
                  <i className="bx bx-repost font-size-16"></i>
                </Button>
                <UncontrolledTooltip placement="top" target={`social-${socialAccount.id}`}>
                  Connect again
                </UncontrolledTooltip>
              </div>
            )}
          </div>
        ),
        signature: (
          <div className="d-flex" style={{ justifyContent: 'space-between' }}>
            <div style={{ width: '30%' }}>{HTMLService.toReact(socialAccount.signature || '')}</div>
            <div>
              <div className="hover-pointer">
                <a
                  className="d-flex mr-2"
                  id={`social-${socialAccount.id}`}
                  onClick={() =>
                    openSelectRTEModal({
                      onSubmit: (content: string) => onUpdateSignature(socialAccount.id, content),
                      title: 'Signature',
                      placeholder: 'Signature',
                      label: 'Signature',
                      initialValue: socialAccount.signature,
                    })
                  }
                >
                  <i className="bx bx-edit font-size-16"></i>
                </a>
              </div>
            </div>
          </div>
        ),
        action: <TableActions onDelete={() => onDelete(socialAccount.id)} />,
      })),
    }),
    [onDelete, onUpdateSignature, user, userId],
  );
  const onConnectClick = useCallback(() => {
    if (allowConnect) {
      return NylasConnectUrl(userId);
    } else {
      openGenericMessageModal({
        title: 'Maximum number of email syncs reached',
        icon: 'fa fa-envelope',
        content: 'You have reached the maximum number of email syncs. Please upgrade your plan to add more.',
      });
    }
  }, [allowConnect, userId]);
  if (loading.loading || !user) return <TopLoadingBar />;

  return (
    <div id="calendar-settings">
      <Row>
        <Col md={6}>
          <h4>Email & Calendar Sync</h4>
        </Col>
        <Col md={6}>
          <Button style={{ float: 'right' }} onClick={onConnectClick}>
            Connect Account
          </Button>
        </Col>
      </Row>
      <br />
      <MDBDataTable responsive striped bordered data={data} paging={false} searching={false} noBottomColumns displayEntries={false}></MDBDataTable>
    </div>
  );
});
